import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiSend, FiUser, FiCalendar,
  FiImage, FiX, FiUpload, FiCheck
} from 'react-icons/fi';
import { api } from '../api';
import toast from 'react-hot-toast';

const GuestBookEntry = ({ entry, index }) => {
  const [imageError, setImageError] = useState(false);
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
      className="bg-white rounded-xl shadow-md p-6 mb-6"
    >
      <div className="flex items-start space-x-4">
        {entry.photo_path && !imageError ? (
          <img
            src={entry.photo_path}
            alt={entry.name}
            onError={() => setImageError(true)}
            className="w-16 h-16 rounded-full object-cover"
          />
        ) : (
          <div className="w-16 h-16 rounded-full bg-lodge-brown-100 flex items-center justify-center">
            <FiUser className="w-8 h-8 text-lodge-brown-600" />
          </div>
        )}
        
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-mountain-gray-800">{entry.name}</h3>
            <div className="flex items-center text-sm text-mountain-gray-500">
              <FiCalendar className="w-4 h-4 mr-1" />
              {new Date(entry.created_at).toLocaleDateString()}
            </div>
          </div>
          
          <p className="text-mountain-gray-700 whitespace-pre-wrap">{entry.message}</p>
          
          {(entry.check_in && entry.check_out) ? (
            <p className="text-sm text-mountain-gray-500 mt-2">
              Stayed: {new Date(entry.check_in).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - {new Date(entry.check_out).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          ) : entry.stay_dates && (
            <p className="text-sm text-mountain-gray-500 mt-2">
              Stayed: {entry.stay_dates}
            </p>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const GuestBookForm = ({ onSuccess }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    stay_dates: '',
    check_in: '',
    check_out: '',
    photo: null
  });
  const [photoPreview, setPhotoPreview] = useState(null);

  const createMutation = useMutation(
    async (data) => {
      const formData = new FormData();
      Object.keys(data).forEach(key => {
        if (data[key] !== null) {
          formData.append(key, data[key]);
        }
      });
      return api.createGuestbookEntry(formData);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('guestbook');
        setFormData({
          name: '',
          email: '',
          message: '',
          stay_dates: '',
          check_in: '',
          check_out: '',
          photo: null
        });
        setPhotoPreview(null);
        if (onSuccess) onSuccess();
      },
      onError: () => toast.error('Failed to submit entry')
    }
  );

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setFormData({ ...formData, photo: file });
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const removePhoto = () => {
    setFormData({ ...formData, photo: null });
    setPhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    createMutation.mutate(formData);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-md p-6 mb-8"
    >
      <h2 className="text-2xl font-display text-lodge-brown-800 mb-6">
        Sign Our Guest Book
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Your Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email (optional)
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-in Date
            </label>
            <input
              type="date"
              value={formData.check_in}
              onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-out Date
            </label>
            <input
              type="date"
              value={formData.check_out}
              onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
              min={formData.check_in}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Your Message *
          </label>
          <textarea
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
            rows="4"
            placeholder="Share your experience at our mountain retreat..."
            required
          />
        </div>

        {/* Photo Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add a Photo (optional)
          </label>
          
          {!photoPreview && (
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                <FiUpload className="w-5 h-5 mr-2" />
                Add Photo
              </button>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {/* Photo Preview */}
          {photoPreview && (
            <div className="relative inline-block">
              <img
                src={photoPreview}
                alt="Preview"
                className="w-32 h-32 object-cover rounded-lg"
              />
              <button
                type="button"
                onClick={removePhoto}
                className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
              >
                <FiX className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={createMutation.isLoading}
            className="btn-primary flex items-center"
          >
            <FiSend className="w-5 h-5 mr-2" />
            {createMutation.isLoading ? 'Submitting...' : 'Submit Entry'}
          </button>
        </div>
      </form>
    </motion.div>
  );
};

const GuestBookPage = () => {
  const [showThankYou, setShowThankYou] = useState(false);
  const { data: entriesResponse, isLoading } = useQuery(
    'guestbook',
    () => api.getGuestbookEntries()
  );
  
  const entries = entriesResponse?.data || [];
  const approvedEntries = entries.filter(entry => entry.is_approved);

  const handleFormSuccess = () => {
    setShowThankYou(true);
    // Auto-hide thank you message after 10 seconds
    setTimeout(() => {
      setShowThankYou(false);
    }, 10000);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h1 className="text-4xl font-display text-lodge-brown-800 mb-4">
          Guest Book
        </h1>
        <p className="text-lg text-mountain-gray-600">
          Share your memories from your stay at our mountain retreat
        </p>
      </motion.div>

      {showThankYou ? (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          className="bg-white rounded-xl shadow-lg p-8 text-center mb-8"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <FiCheck className="w-10 h-10 text-green-600" />
          </motion.div>
          <h2 className="text-2xl font-display text-lodge-brown-800 mb-3">
            Thank You!
          </h2>
          <p className="text-mountain-gray-600 mb-6">
            Your entry has been submitted and will appear once approved.
          </p>
          <button
            onClick={() => setShowThankYou(false)}
            className="btn-secondary"
          >
            Add Another Entry
          </button>
        </motion.div>
      ) : (
        <GuestBookForm onSuccess={handleFormSuccess} />
      )}

      {/* Entries */}
      <div>
        <h2 className="text-2xl font-display text-lodge-brown-800 mb-6">
          Entries
        </h2>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
          </div>
        ) : approvedEntries.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-gray-50 rounded-xl"
          >
            <FiImage className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Be the first to sign our guest book!</p>
          </motion.div>
        ) : (
          <AnimatePresence>
            {approvedEntries.map((entry, index) => (
              <GuestBookEntry key={entry.id} entry={entry} index={index} />
            ))}
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default GuestBookPage;